import { FC } from 'react';

import { toast } from 'sonner';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';

import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';

import { SpinBannerForm as SpinBannerFormValues } from './form';
import { SpecialDropsSection } from './specialDrops';
import { SpecialDropSettingsForm as SpecialDropSettingsFormValues } from './specialDropSettingsForm/form';
import { SpecialDropSettingsForm } from './specialDropSettingsForm/SpecialDropSettingsForm';
import { SpinBannerForm } from './spinBannerForm/SpinBannerForm';
import { getFormValuesFromModel, getRequestFromForm } from './utils';

import css from './SpinBannerSettingsPage.module.css';

import { spinBannerService } from 'v2/api/spinBannerService';
import { GeoContextBadge } from 'v2/components/geoContextBadge/GeoContextBadge';
import { Loader } from 'v2/components/loader/Loader';
import { SpecialDropType } from 'v2/types/SpecialDropModel';
import { handleHttpError } from 'v2/utils';

export const SpinBannerSettingsPage: FC = () => {
  const {
    data: spinBannerSettings,
    isLoading: isSpinBannerLoading,
    mutate: mutateSpinBanner,
  } = useSWR('getSpinBannerSettings', spinBannerService.getSpinBannerSettings);

  const { trigger: triggerUpdateSpinBanner, isMutating: isSpinBannerMutating } = useSWRMutation(
    'updateSpinBannerSettings',
    spinBannerService.updateSpinBannerSettings,
    {
      onSuccess: () => {
        toast.success('Spin banner settings have been successfully updated');
        mutateSpinBanner();
      },
      onError: handleHttpError,
    }
  );

  const {
    data: specialDropSettings,
    isLoading: isSpecialDropSettingsLoading,
    mutate: mutateSpecialDropSettings,
  } = useSWR('getSpecialDropSettings', spinBannerService.getSpecialDropSettings);

  const { trigger: triggerUpdateSpecialDropSettings, isMutating: isSpecialDropSettingsMutating } = useSWRMutation(
    'updateSpecialDropSettings',
    spinBannerService.updateSpecialDropSettings,
    {
      onSuccess: () => {
        toast.success('Special drop settings have been successfully updated');
        mutateSpecialDropSettings();
      },
      onError: handleHttpError,
    }
  );

  const handleUpdateSpinBanner = async (values: SpinBannerFormValues) => {
    try {
      await triggerUpdateSpinBanner(getRequestFromForm(values));
    } catch {
      // error is handled by onError
    }
  };

  const handleUpdateSpecialDropSettings = async (values: SpecialDropSettingsFormValues) => {
    try {
      await triggerUpdateSpecialDropSettings(values);
    } catch {
      // error is handled by onError
    }
  };

  return (
    <div className={css.spinBannerSettingsPage}>
      <GeoContextBadge forceShow />

      {isSpinBannerLoading || !spinBannerSettings ? (
        <Loader />
      ) : (
        <SpinBannerForm
          initialValues={getFormValuesFromModel(spinBannerSettings)}
          isSubmitting={isSpinBannerMutating}
          onSubmit={handleUpdateSpinBanner}
        />
      )}

      <div className={css.specialDropSettingsSection}>
        <Typography fontSize="24px" fontWeight={600}>
          Wheel of Fortune Settings
        </Typography>

        {isSpecialDropSettingsLoading || !specialDropSettings ? (
          <Loader />
        ) : (
          <SpecialDropSettingsForm
            initialValues={{
              minBalanceRewardAmount: specialDropSettings.minBalanceRewardAmount,
              maxBalanceRewardAmount: specialDropSettings.maxBalanceRewardAmount,
              secondSpinOptions: specialDropSettings.secondSpinOptions,
            }}
            isSubmitting={isSpecialDropSettingsMutating}
            onSubmit={handleUpdateSpecialDropSettings}
          />
        )}
      </div>

      <SpecialDropsSection
        type={SpecialDropType.SPIN_BANNER}
        title="Special Drop"
        note={
          <Alert severity="info" variant="filled">
            Настройка ниже распространяется на все гео
          </Alert>
        }
      />
    </div>
  );
};
